import {
  Device,
  Field,
  ForecastPageLimitsResponse,
  reduceForecastTemplateLimits,
  usePowerLevels,
} from '@e2m-customer-portal/hooks';
import { getInputFieldSiblingType } from '@e2m-customer-portal/shared';
import { ForecastTemplateInfo, ProductInput } from '@e2m-customer-portal/ui';
import { Grid, Typography } from '@mui/material';
import { Interval } from 'date-fns';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { DefaultForecastLimitsQuery } from '../../../generated/graphql';

export interface ForecastAssistantInputsProps {
  device: Device;
  templateLimits: DefaultForecastLimitsQuery['getDefaultForecast'];
  fieldLimits: ForecastPageLimitsResponse;
  disabled: boolean;
  interval: Interval;
  fields: readonly Field[];
  isControlEnergy: boolean;
  onChange: (fields: readonly Field[]) => void;
}

export function ForecastAssistantInputs({
  device,
  templateLimits,
  fieldLimits,
  disabled,
  interval,
  fields,
  isControlEnergy,
  onChange,
}: Readonly<ForecastAssistantInputsProps>): ReactElement<ForecastAssistantInputsProps> {
  const [t] = useTranslation();
  const { minimum, maximum, auxiliaryConsumption } = reduceForecastTemplateLimits(templateLimits, interval);
  const powerLevels = usePowerLevels(device.id) ?? [];

  return (
    <>
      <Grid item xs={12}>
        <Typography variant="body1">{t('FORECAST_ASSISTANT_FORECAST')}</Typography>
      </Grid>

      {fields.map((field) => {
        const siblingType = getInputFieldSiblingType(field.type);
        const hasSibling = siblingType && fieldLimits.fields.some((f) => f.type === siblingType);

        return (
          <Grid item key={field.type} xs={hasSibling ? 6 : 12}>
            <ProductInput
              fullWidth
              disabled={disabled}
              type={field.type}
              fields={fields}
              onChange={onChange}
              date={new Date(interval.start)}
              isControlEnergy={isControlEnergy}
              deadlines={device.deadlines}
              limits={fieldLimits}
              lowerLimit={minimum}
              upperLimit={maximum}
              powerLevels={powerLevels}
            />
          </Grid>
        );
      })}

      {!isControlEnergy && (
        <Grid item xs={12}>
          <ForecastTemplateInfo lowerLimit={minimum} upperLimit={maximum} auxiliary={auxiliaryConsumption} />
        </Grid>
      )}
    </>
  );
}
